
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Report } from "@/types/report";

// Define a type for the raw data from Supabase
interface RawReportData {
  id: string;
  teaching_date: string;
  teaching_time_slot: string;
  student_nickname: string;
  student_full_name: string;
  student_grade_level: string;
  teacher_display: string;
  teacher_id: string;
  subject: string;
  worksheet_urls: string[];
  worksheet_drive_link?: string;
  development_report?: string;
  teaching_content?: string;
  approved_worksheet_status?: string;
  approved_worksheet_at?: string;
  approved_worksheet_by?: string;
  approved_worksheet_note?: string;
  other_subject?: boolean;
}

export const useAdminWorksheetCheck = () => {
  const [isLoading, setIsLoading] = useState(false);

  const query = useQuery({
    queryKey: ["admin-worksheet-check"],
    queryFn: async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("student_development_reports")
          .select(
            "id, teaching_date, teaching_time_slot, student_nickname, student_full_name, student_grade_level, teacher_display, teacher_id, subject, worksheet_urls, worksheet_drive_link, development_report, teaching_content, approved_worksheet_status, approved_worksheet_at, approved_worksheet_by, approved_worksheet_note, other_subject"
          )
          .filter("worksheet_urls", "not.is", null)
          .not("worksheet_urls", "eq", "{}")
          .order("created_at", { ascending: false });

        if (error) {
          console.error('Error fetching worksheet check data:', error);
          return [];
        }
        const formattedData = (data || []).map((report: RawReportData) => ({
          ...report,
          topic_learned: report.teaching_content || "-",
          created_at: "",
          status: "",
          start_date: null,
          end_date: null,
          approved_worksheet_status: report.approved_worksheet_status === "APPROVED"
            ? "APPROVED"
            : report.approved_worksheet_status === "REJECTED"
            ? "REJECTED"
            : "WAITING"
        }));

        return formattedData;
      } catch (error) {
        console.error('Error fetching worksheet check data:', error);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
  });

  return {
    ...query,
    isLoading: query.isLoading || isLoading,
    reports: (query.data || []) as unknown as Report[],
    refetch: query.refetch,
  };
};
